import { FolderContent } from '@/api/generatedApi';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { LIST_FOLDER_CONTENTS_QUERY_KEY } from '@/hooks/api/folders/useFetchFolderContents';
import { GET_FOLDER_HIERARCHY_QUERY_KEY } from '@/hooks/api/folders/useFolderHierarchy';
import { GET_FOLDER_QUERY_KEY } from '@/hooks/api/folders/useGetFolder';
import { useToast } from '@/hooks/useToast';
import { pluralize } from '@/utils/format';
import { ContentMoveState, toApiFolderId, getContentOriginalParentId } from './utils';

export const useBulkMoveContents = ({
  contents,
  onMoveComplete,
}: {
  contents: FolderContent[];
  onMoveComplete?: () => void;
}) => {
  const { getTenantId } = useTenants();
  const tenantId = getTenantId();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const [moveStates, setMoveStates] = useState<ContentMoveState[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isMoveComplete, setIsMoveComplete] = useState(false);
  const abortControllerReference = useRef<AbortController | undefined>(undefined);

  const folderCount = contents.filter(c => c.contentType === 'folder').length;

  const initializeMoveStates = () => {
    setMoveStates(
      contents.map(content => ({
        content,
        originalParentId: getContentOriginalParentId(content),
        status: 'pending',
      }))
    );
  };

  const invalidateQueries = (includesFolders: boolean) => {
    queryClient.resetQueries({
      queryKey: [LIST_FOLDER_CONTENTS_QUERY_KEY],
      exact: false,
    });
    if (includesFolders) {
      queryClient.removeQueries({
        queryKey: [GET_FOLDER_QUERY_KEY],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: [GET_FOLDER_HIERARCHY_QUERY_KEY],
        exact: false,
      });
    }
  };

  const undoMoves = async (moves: ContentMoveState[]) => {
    let movedFolders = false;
    let failedCount = 0;
    for (const { content, originalParentId } of moves) {
      try {
        if (content.contentType === 'folder') {
          await api.folders.moveFolder(tenantId, content.id, { parentFolderId: toApiFolderId(originalParentId) });
          movedFolders = true;
        } else {
          await api.assets.moveAsset(tenantId, content.id, { folderId: toApiFolderId(originalParentId) });
        }
      } catch {
        failedCount += 1;
      }
    }
    invalidateQueries(movedFolders);
    if (failedCount > 0) {
      showToast({
        message: `Failed to undo ${pluralize(failedCount, 'item', 'items')}`,
        type: 'error',
      });
    }
  };

  const startMove = async (targetFolderId: string | undefined) => {
    const moveTargetFolderId = toApiFolderId(targetFolderId);

    setIsProcessing(true);
    initializeMoveStates();

    const abortController = new AbortController();
    abortControllerReference.current = abortController;

    let successCount = 0;
    let errorCount = 0;
    const successfulMoves: ContentMoveState[] = [];

    for (const content of contents) {
      if (abortController.signal.aborted) {
        break;
      }

      const originalParentId = getContentOriginalParentId(content);

      setMoveStates(previous =>
        previous.map(state => (state.content.id === content.id ? { ...state, status: 'processing' } : state))
      );

      try {
        await (content.contentType === 'folder'
          ? api.folders.moveFolder(tenantId, content.id, { parentFolderId: moveTargetFolderId })
          : api.assets.moveAsset(tenantId, content.id, { folderId: moveTargetFolderId }));

        if (!abortController.signal.aborted) {
          setMoveStates(previous =>
            previous.map(state => (state.content.id === content.id ? { ...state, status: 'success' } : state))
          );
          successCount += 1;
          successfulMoves.push({ content, originalParentId, status: 'success' });
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          const message =
            (error as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Failed to move item';
          setMoveStates(previous =>
            previous.map(state =>
              state.content.id === content.id ? { ...state, status: 'error', error: message } : state
            )
          );
          errorCount += 1;
        }
      }
    }

    if (!abortController.signal.aborted) {
      setIsProcessing(false);
      invalidateQueries(folderCount > 0);

      if (successCount > 0) {
        showToast({
          message: `Moved ${pluralize(successCount, 'item', 'items')}`,
          type: 'success',
          action: {
            label: 'Undo',
            onClick: () => {
              void undoMoves(successfulMoves);
            },
          },
        });
      }

      if (errorCount > 0) {
        showToast({
          message: `Failed to move ${pluralize(errorCount, 'item', 'items')}`,
          type: 'error',
          action:
            successfulMoves.length > 0
              ? {
                  label: 'Undo',
                  onClick: () => {
                    void undoMoves(successfulMoves);
                  },
                }
              : undefined,
        });
      }

      setIsMoveComplete(true);
      onMoveComplete?.();
    }

    abortControllerReference.current = undefined;
  };

  const abort = () => {
    if (abortControllerReference.current) {
      abortControllerReference.current.abort();
    }
  };

  const reset = () => {
    setMoveStates([]);
    setIsMoveComplete(false);
  };

  const completedCount = moveStates.filter(state => state.status === 'success' || state.status === 'error').length;
  const progress = moveStates.length > 0 ? (completedCount / moveStates.length) * 100 : 0;

  return {
    moveStates,
    isProcessing,
    isMoveComplete,
    completedCount,
    progress,
    startMove,
    abort,
    reset,
  };
};
